'use client';

import { Skeleton } from '@mui/material';
import clsx from 'clsx';

import { useGetJobsQuery } from '@/features/jobs/api/jobsApi';
import { useGetWorkspaceMembersQuery } from '@/features/workspaces/api/workspacesApi';
import { CloseIcon, Divider } from '@web/ui';

import styles from './ChatPanel.module.scss';

import type { ChatFilters } from '@/app/(protected)/[workspace_slug]/chat/ChatContext';
import type { Job } from '@/features/jobs/types';
import type { WorkspaceMemberItem } from '@/features/workspaces/types';

function getRows<T>(data: { results?: T[] } | T[] | null | undefined) {
  if (Array.isArray(data)) {
    return data;
  }

  return data?.results ?? [];
}

function getMemberLabel(member: WorkspaceMemberItem) {
  return (
    `${member.user.first_name ?? ''} ${member.user.last_name ?? ''}`.trim() ||
    member.user.email ||
    'Unknown user'
  );
}

export function ChatFiltersPanel({
  open,
  filters,
  onChangeFilters,
  onClose,
}: {
  open: boolean;
  filters: ChatFilters;
  onChangeFilters: (filters: ChatFilters) => void;
  onClose: () => void;
}) {
  const { data: jobsData, isLoading: isJobsLoading } = useGetJobsQuery({
    status: 'active,completed,pending',
    page: 1,
    page_size: 25,
  });
  const { data: membersData, isLoading: isMembersLoading } =
    useGetWorkspaceMembersQuery({
      status: 'active',
      page: 1,
      page_size: 25,
    });
  const jobs = getRows<Job>(jobsData?.data);
  const members = getRows<WorkspaceMemberItem>(membersData?.data);

  return (
    <>
      {open ? (
        <button
          type="button"
          className={styles.chatPanel__sidePanelBackdrop}
          aria-label="Close filters"
          onClick={onClose}
        />
      ) : null}
      <aside
        className={clsx('card-container', styles.chatPanel__sidePanel, {
          [styles.chatPanel__sidePanelOpen]: open,
        })}
        aria-label="Chat filters"
      >
        <div className={styles.chatPanel__sidePanelHeader}>
          <div>
            <strong>Filter Chat</strong>
            <p>Choose work or people</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close filters">
            <CloseIcon size={14} />
          </button>
        </div>

        <div className={styles.chatPanel__sidePanelBody}>
          <section className={styles.chatPanel__destinationGroup}>
            <div className={styles.chatPanel__destinationGroupHeader}>Jobs</div>
            {isJobsLoading ? (
              <>
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} variant="rounded" height={32} />
                ))}
              </>
            ) : jobs.length ? (
              jobs.map((job) => (
                <button
                  key={job.id}
                  type="button"
                  className={clsx(styles.chatPanel__destination, {
                    [styles.chatPanel__destinationActive]:
                      filters.workContextType === 'job' &&
                      filters.workContextId === job.id,
                  })}
                  onClick={() =>
                    onChangeFilters({
                      ...filters,
                      workContextType: 'job',
                      workContextId: job.id,
                      workContextLabel: job.name,
                    })
                  }
                >
                  <span>{job.name}</span>
                </button>
              ))
            ) : (
              <p className={styles.chatPanel__sidePanelEmpty}>
                No jobs available.
              </p>
            )}
            {filters.workContextId ? (
              <button
                type="button"
                className={styles.chatPanel__clearFilterButton}
                onClick={() =>
                  onChangeFilters({
                    ...filters,
                    workContextType: undefined,
                    workContextId: undefined,
                    workContextLabel: undefined,
                  })
                }
              >
                Clear job filter
              </button>
            ) : null}
          </section>

          <Divider />

          <section className={styles.chatPanel__destinationGroup}>
            <div className={styles.chatPanel__destinationGroupHeader}>
              People
            </div>
            {isMembersLoading ? (
              <>
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} variant="rounded" height={32} />
                ))}
              </>
            ) : members.length ? (
              members.map((member) => {
                const label = getMemberLabel(member);

                return (
                  <button
                    key={member.id}
                    type="button"
                    className={clsx(styles.chatPanel__destination, {
                      [styles.chatPanel__destinationActive]:
                        filters.userId === member.user.id,
                    })}
                    onClick={() =>
                      onChangeFilters({
                        ...filters,
                        userId: member.user.id,
                        userLabel: label,
                      })
                    }
                  >
                    <span>{label}</span>
                  </button>
                );
              })
            ) : (
              <p className={styles.chatPanel__sidePanelEmpty}>
                No people available.
              </p>
            )}
            {filters.userId ? (
              <button
                type="button"
                className={styles.chatPanel__clearFilterButton}
                onClick={() =>
                  onChangeFilters({
                    ...filters,
                    userId: undefined,
                    userLabel: undefined,
                  })
                }
              >
                Clear people filter
              </button>
            ) : null}
          </section>
        </div>
      </aside>
    </>
  );
}
